import React, { useState, useCallback, useMemo } from 'react';
import { Card, Table, Button, message, DatePicker } from 'antd';
import { SearchOutlined, ExportOutlined } from '@ant-design/icons';
import { useQuery, useMutation, gql } from '@apollo/client';
import { ClientSugSelector } from '../../../components/settlement/ClientSugSelector';
import TablePaginationBar from '../../../components/TablePaginationBar';
import ParcelsDownloadModal from './components/ParcelsDownloadModal';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getCurrencyName } from '../../../utils/settlementAmountFormatter';
import { resolvePricingSchemeMessage } from '../../../utils/graphqlUtils';
dayjs.extend(utc);
dayjs.extend(timezone);
const { RangePicker } = DatePicker;

interface BillingPeriodReceivableFilterInput {
    settlementClientId?: number;
    periodStart?: number;
    periodEnd?: number;
}

interface BillingPeriodReceivableType {
    clientId: number;
    clientName: string;
    billId: number | null;
    billStatus: number | null;
    currency: string;
    orderCount: number;
    billAmount: number;
    invoicedAmount: number;
    lastInvoiceTime: string | null;
    receivedAmount: number;
    lastReceiptDate: number | null;
    periodStart: string | null;
    periodEnd: string | null;
    periodTimezone: string;
}

const PAGE_QUERY_BILLING_PERIOD_RECEIVABLE = gql`
  query PageQueryBillingPeriodReceivable(
    $filters: BillingPeriodReceivableFilterInput
    $page: Int!
    $pageSize: Int!
  ) {
    pageQueryBillingPeriodReceivable(filters: $filters, page: $page, pageSize: $pageSize) {
      list {
        clientId
        clientName
        billId
        billStatus
        currency
        periodStart
        periodEnd
        periodTimezone
        orderCount
        billAmount
        invoicedAmount
        lastInvoiceTime
        receivedAmount
        lastReceiptDate
        baseFreight
        baseFreightAdj
        otherAdj
        otherAdjAdj
        overLengthFee
        overLengthFeeAdj
        overWeightFee
        overWeightFeeAdj
        deliveryFeeTt
        deliveryFeeTtAdj
        returnFeeTt
        claimAdj
        claimAdjAdj
        otherDecrease
        otherDecreaseAdj
      }
      total
    }
  }
`;

const EXPORT_BILLING_PERIOD_RECEIVABLE = gql`
  mutation ExportBillingPeriodReceivable($filters: BillingPeriodReceivableFilterInput) {
    exportBillingPeriodReceivable(filters: $filters) {
      success
      message
      messageKey
      data
    }
  }
`;
const formatPeriodRange = (
    periodStart: string | null | undefined,
    periodEnd: string | null | undefined,
    periodTimezone: string = 'UTC'
): string => {
    if (!periodStart || !periodEnd) return '-';

    try {
        const startDate = dayjs(periodStart).tz(periodTimezone).format('YYYY.MM.DD');
        const endDate = dayjs(periodEnd).tz(periodTimezone).format('YYYY.MM.DD');

        return `${startDate}-${endDate}`;
    } catch (e) {
        return '-';
    }
};
const BillPeriodEstimatedReceivablePage: React.FC = () => {
    const [filters, setFilters] = useState<BillingPeriodReceivableFilterInput>({});
    const [tempFilters, setTempFilters] = useState<BillingPeriodReceivableFilterInput>({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');

    const { data, loading } = useQuery(PAGE_QUERY_BILLING_PERIOD_RECEIVABLE, {
        variables: {
            filters: {
                settlementClientIds: filters.settlementClientId ? [filters.settlementClientId] : undefined,
                periodStart: filters.periodStart,
                periodEnd: filters.periodEnd,
            },
            page,
            pageSize,
        },
        fetchPolicy: 'cache-and-network',
    });

    const [exportBillingPeriodReceivable] = useMutation(EXPORT_BILLING_PERIOD_RECEIVABLE);

    const handleSearch = useCallback(() => {
        setFilters(tempFilters);
        setPage(1);
    }, [tempFilters]);

    const handleReset = () => {
        setTempFilters({});
        setFilters({});
        setPage(1);
    };

    const handleExport = async () => {
        try {
            const result = await exportBillingPeriodReceivable({
                variables: {
                    filters: {
                        settlementClientIds: filters.settlementClientId ? [filters.settlementClientId] : undefined,
                        periodStart: filters.periodStart,
                        periodEnd: filters.periodEnd,
                    },
                },
            });

            const exportResult = result.data?.exportBillingPeriodReceivable;
            const fileName = exportResult?.data
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadModalVisible(true);
            } else {
                message.error(resolvePricingSchemeMessage(exportResult, '导出失败'));
            }
        } catch (error) {
            message.error('导出失败: ' + (error as Error).message);
        }
    };

    const parseFeeItems = (feeItemsJson: string): Record<string, number> => {
        try {
            return JSON.parse(feeItemsJson) || {};
        } catch {
            return {};
        }
    };


    const columns = [

        {
            title: '客户名称',
            dataIndex: 'clientName',
            key: 'clientName',
            width: 150,
            fixed: 'left' as const,
        },
        {
            title: '客户ID',
            dataIndex: 'clientId',
            key: 'clientId',
            width: 100,
            fixed: 'left' as const,
        },
        {
            title: '账期',
            key: 'periodRange',
            width: 180,
            render: (_: any, record: BillingPeriodReceivableType) => {
                return formatPeriodRange(record.periodStart, record.periodEnd, record.periodTimezone);
            },
        },
        {
            title: '账单ID',
            dataIndex: 'billId',
            key: 'billId',
            width: 120,
        },
        {
            title: '账单状态',
            dataIndex: 'billStatus',
            key: 'billStatus',
            width: 100,
            render: (status: number | null) => {
                const statusMap: Record<number, string> = {
                    0: '账单初始化',
                    1: '账单汇总待出账',
                    2: '账单已出账',
                    3: '账单调账待确认',
                    4: '账单已内部确认',
                    5: '账单锁定',
                    6: '账单已申诉驳回',
                    7: '账单已外部确认',
                };
                return status !== null ? statusMap[status] || status : '-';
            },
        },
        {
            title: '币种',
            dataIndex: 'currency',
            key: 'currency',
            width: 80,
            render: (currency: string) => getCurrencyName(currency),
        },
        {
            title: '时区',
            dataIndex: 'periodTimezone',
            key: 'periodTimezone',
            width: 150,
        },
        {
            title: '订单数',
            dataIndex: 'orderCount',
            key: 'orderCount',
            width: 100,
        },
        {
            title: '账单金额',
            dataIndex: 'billAmount',
            key: 'billAmount',
            width: 140,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '已开票金额',
            dataIndex: 'invoicedAmount',
            key: 'invoicedAmount',
            width: 140,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '最后一次开票时间',
            dataIndex: 'lastInvoiceTime',
            key: 'lastInvoiceTime',
            width: 180,
            render: (date: number | null) => {
                if (!date) return '-';
                const dateStr = date.toString();
                if (dateStr.length === 8) {
                    return `${dateStr.substring(0, 4)}.${dateStr.substring(4, 6)}.${dateStr.substring(6, 8)}`;
                }
                return date;
            },
        },
        {
            title: '已回款金额',
            dataIndex: 'receivedAmount',
            key: 'receivedAmount',
            width: 140,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '最后一次回款日期',
            dataIndex: 'lastReceiptDate',
            key: 'lastReceiptDate',
            width: 140,
            render: (date: number | null) => {
                if (!date) return '-';
                const dateStr = date.toString();
                if (dateStr.length === 8) {
                    return `${dateStr.substring(0, 4)}.${dateStr.substring(4, 6)}.${dateStr.substring(6, 8)}`;
                }
                return date;
            },
        },
        {
            title: '客户基础运费',
            dataIndex: 'baseFreight',
            key: 'baseFreight',
            width: 140,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '客户基础运费-调整',
            dataIndex: 'baseFreightAdj',
            key: 'baseFreightAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '其他调增',
            dataIndex: 'otherAdj',
            key: 'otherAdj',
            width: 120,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '其他调增-调整',
            dataIndex: 'otherAdjAdj',
            key: 'otherAdjAdj',
            width: 140,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '客户超长费',
            dataIndex: 'overLengthFee',
            key: 'overLengthFee',
            width: 130,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '客户超长费调整',
            dataIndex: 'overLengthFeeAdj',
            key: 'overLengthFeeAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '客户超重费',
            dataIndex: 'overWeightFee',
            key: 'overWeightFee',
            width: 130,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '客户超重费-调整',
            dataIndex: 'overWeightFeeAdj',
            key: 'overWeightFeeAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '配送费(TT)',
            dataIndex: 'deliveryFeeTt',
            key: 'deliveryFeeTt',
            width: 130,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '配送费(TT)-调整',
            dataIndex: 'deliveryFeeTtAdj',
            key: 'deliveryFeeTtAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '退货费（TT）',
            dataIndex: 'returnFeeTt',
            key: 'returnFeeTt',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '理赔调整',
            dataIndex: 'claimAdj',
            key: 'claimAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '理赔调整-调整',
            dataIndex: 'claimAdjAdj',
            key: 'claimAdjAdj',
            width: 150,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '其他调减',
            dataIndex: 'otherDecrease',
            key: 'otherDecrease',
            width: 120,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
        {
            title: '其他调减-调整',
            dataIndex: 'otherDecreaseAdj',
            key: 'otherDecreaseAdj',
            width: 140,
            render: (amount: number) => `${(amount / 100).toFixed(2)}`,
        },
    ];

    return (
        <div className="p-6 space-y-4">
            <Card title="筛选条件">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">客户名称</label>
                        <ClientSugSelector
                            roleType={0}
                            placeholder="搜索客户..."
                            value={tempFilters.settlementClientId != null ? String(tempFilters.settlementClientId) : undefined}
                            onChange={(val) => {
                                setTempFilters((prev) => ({
                                    ...prev,
                                    settlementClientId: val ? Number(val) : undefined,
                                }));
                            }}
                            className="w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">账期时间范围</label>
                        <RangePicker
                            className="w-full"
                            value={
                                tempFilters.periodStart && tempFilters.periodEnd
                                    ? [
                                        dayjs(tempFilters.periodStart.toString(), 'YYYYMMDD'),
                                        dayjs(tempFilters.periodEnd.toString(), 'YYYYMMDD'),
                                    ] as [Dayjs, Dayjs]
                                    : null
                            }
                            onChange={(dates) => {
                                if (dates?.[0] && dates?.[1]) {
                                    setTempFilters((prev) => ({
                                        ...prev,
                                        periodStart: parseInt(dates[0]!.format('YYYYMMDD')),
                                        periodEnd: parseInt(dates[1]!.format('YYYYMMDD')),
                                    }));
                                } else {
                                    setTempFilters((prev) => ({
                                        ...prev,
                                        periodStart: undefined,
                                        periodEnd: undefined,
                                    }));
                                }
                            }}
                            format="YYYY-MM-DD"
                        />
                    </div>
                    <div className="flex items-end gap-2">
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            className="flex-1"
                        >
                            查询
                        </Button>
                        <Button onClick={handleReset} className="flex-1">
                            重置
                        </Button>
                    </div>
                    <div className="flex items-end">
                        <Button
                            type="default"
                            icon={<ExportOutlined />}
                            onClick={handleExport}
                            disabled={!data?.pageQueryBillingPeriodReceivable?.total}
                        >
                            导出
                        </Button>
                    </div>
                </div>
            </Card>

            <Card title="预估应收列表（账期维度）">
                <Table
                    columns={columns}
                    dataSource={data?.pageQueryBillingPeriodReceivable?.list || []}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 3260 }}
                    className="mb-4"
                />

                <TablePaginationBar
                    total={data?.pageQueryBillingPeriodReceivable?.total || 0}
                    current={page}
                    pageSize={pageSize}
                    onChange={(newPage, newPageSize) => {
                        setPage(newPage);
                        setPageSize(newPageSize);
                    }}
                />
            </Card>

            <ParcelsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default BillPeriodEstimatedReceivablePage;
